<script>
import { win } from '@rei/browser-shim';
import { CdrText, IconLocationPinFill } from '@rei/cedar';
import { LocationSearch } from '@rei/location-search';
import queryString from 'query-string';
import constants from '../common/constants';
import metrics from '../mixins/metrics';

export default {
  name: 'LocationSearchFacet',
  components: {
    CdrText,
    IconLocationPinFill,
    LocationSearch,
  },
  mixins: [metrics],
  props: {
    showChallenger: {
      type: Boolean,
      default: false,
    },
    location: {
      type: String,
      required: true,
    },
    displayedLocation: {
      type: String,
      default: null,
      required: false,
    },
    radius: {
      type: Number,
      required: true,
    },
    mobile: {
      type: Boolean,
      default: null,
      required: false,
    },
    api: {
      type: String,
      default: constants.API.AUTOCOMPLETE,
      required: false,
    },
    hideHeading: {
      type: Boolean,
      default: false,
      required: false,
    },
    page: {
      type: String,
      default: 'event-search',
      required: false,
    },
    validation: {
      type: Boolean,
      default: true,
    },
    validationPattern: {
      type: RegExp,
      default: () => constants.REGEX.LOCATION_VALID,
    },
  },
  emits: ['updateLocation', 'removeLocation'],
  data: () => ({
    locationWasUpdated: false,
    radiusWasUpdated: false,
  }),
  computed: {
    label() {
      const { page, showChallenger } = this;

      if (!showChallenger) {
        return page === 'course' ? 'Filter by location' : 'Location';
      }

      const { location } = this;
      return location ? '' : 'Enter a location to find a nearby session.';
    },
    mainClasses() {
      const { page, location, showChallenger } = this;

      return [
        'location-search',
        {
          'location-search--course': page === 'course',
          'location-search--has-location': showChallenger && !!location,
          'location-search--control': !showChallenger,
        },
      ];
    },
    pushpopRemoveParams() {
      const { page } = this;
      const searchParams = 'page,previousLocation,course.session.anyLocation,distance,location';
      const courseParams = 'course.session.anyLocation,distance,geoLocationId';
      return page === 'course' ? courseParams : searchParams;
    },
  },
  methods: {
    isLocationValid(location) {
      const locationValue = location.trim();
      let isValid = false;
      if (constants.REGEX.LOCATION_VALID.test(locationValue)) {
        isValid = true;
      }
      return isValid;
    },
    updateLocation(searchedString, radius, inputData) {
      this.$emit('updateLocation', searchedString, radius, inputData);
      if (this.isLocationValid(searchedString)) {
        this.currentRadius = parseInt(radius, 10);
        const parsedQueryString = queryString.parse(win.location.search);
        if (inputData) {
          let googlePlaceId = null;
          const { placeId } = inputData;
          if (placeId) {
            googlePlaceId = placeId;
          } else if (inputData.place_id) {
            googlePlaceId = inputData.place_id;
          }
          parsedQueryString.geoLocationId = googlePlaceId;
        } else {
          parsedQueryString.location = searchedString;
        }
        parsedQueryString.distance = radius;

        // remove old location query params
        const removeParams = ['previousLocation', 'course.session.anyLocation', 'page'];
        removeParams.forEach((param) => {
          if (parsedQueryString[param]) {
            delete parsedQueryString[param];
          }
        });

        // ensure + is properly encoded in date/time
        const newQueryString = queryString.stringify(parsedQueryString).replace('+', '%2B');
        // eslint-disable next-line
        const locationUrl =
          newQueryString !== null ? `${win.location.pathname}?${newQueryString}` : null;

        // only update url if it's different than the current url
        if (locationUrl && locationUrl !== win.location.pathname + win.location.search) {
          // ensure current url matches request
          if (win.history.pushState) {
            win.history.replaceState(null, searchedString, locationUrl);
            // the following line is required for the 'queryString' prop to appear correctly
            // in the subsequent ajax request
            if (win && win.appObservable) {
              win.appObservable.pushStateChange();
            }
          } else {
            win.location.assign(locationUrl);
          }
        }
      }
      this.metricsClick({
        interPageEvent: 'true',
        events: 'event160',
        linkName: '{{ pageName }}_location_update location',
      });
      this.locationWasUpdated = true;
      setTimeout(() => {
        this.locationWasUpdated = false;
      }, 100);
    },
    closeModal() {
      const { locationWasUpdated } = this;
      // only fires if location overlay was closed without updating
      if (!locationWasUpdated) {
        this.metricsClick({ linkName: '{{ pageName }}_location_close location modal' });
      }
    },
    openModal() {
      const { location } = this;
      const action = location ? 'change' : 'add';
      this.metricsClick({ linkName: `{{ pageName }}_location_${action} location` });
    },
    handleRadiusChange(radius) {
      const { radiusWasUpdated } = this;
      // LocationSearch triggers the "radius-change" event twice in succession.
      // Prevent double-fire of radius analytics call
      if (!radiusWasUpdated) {
        this.metricsClick({ linkName: `{{ pageName }}_location_change radius_${radius}` });
        this.radiusWasUpdated = true;
        setTimeout(() => {
          this.radiusWasUpdated = false;
        }, 100);
      }
    },
    // TODO: Remove the following function when A/B test ends and target wins
    removeLocation() {
      this.$emit('removeLocation');
      this.$refs.pushpopRemoveLocation.click();
      this.metricsClick({ linkName: '{{ pageName }}_location_remove location' });
    },
    geolocateRequested() {
      this.metricsClick({ linkName: '{{ pageName }}_location_geolocation requested' });
    },
  },
};
</script>
<template>
  <div :class="mainClasses">
    <a
      ref="pushpopRemoveLocation"
      :data-pushpop-remove-params="pushpopRemoveParams"
      data-pushpop-remove-slugs="p"
      data-pushpop-scroll-to="top"
      style="display: none"
      aria-hidden="true"
    />
    <icon-location-pin-fill
      v-if="page === 'course'"
      class="location-search__icon"
    />
    <!-- eslint-disable vue/no-mutating-props -->
    <location-search
      :ref="`location-search-${mobile ? 'mobile' : 'desktop'}`"
      :model-value="location"
      :class="['location-search-facet', { 'location-search-facet__mini': hideHeading }]"
      :radius="radius"
      :autocomplete-fetch-url="api"
      :autocomplete-delay="200"
      :data-ui-add-location-btn="`${mobile ? 'mobile-' : ''}add-location-btn`"
      :data-ui-change-location-btn="`${mobile ? 'mobile-' : ''}facet-change-location-btn`"
      :data-ui-modal-close-modal-btn="`${mobile ? 'mobile-' : ''}location-modal-close-btn`"
      :data-ui-modal-geolocate-btn="`${mobile ? 'mobile-' : ''}location-modal-geolocation-btn`"
      :data-ui-modal-input="`${mobile ? 'mobile-' : ''}location-modal-autocomplete-input`"
      :data-ui-modal-mobile-cancel-btn="`${
        mobile ? 'mobile-' : ''
      }location-modal-mobile-cancel-btn`"
      :data-ui-modal-radius-select="`${mobile ? 'mobile-' : ''}location-modal-radius-select`"
      :data-ui-modal-update-btn="`${mobile ? 'mobile-' : ''}location-modal-update-btn`"
      :data-ui-searched-location="`${mobile ? 'mobile-' : ''}facet-searched-location`"
      :data-ui-searched-radius="`${mobile ? 'mobile-' : ''}facet-searched-radius`"
      :remove-location-enabled="showChallenger ? false : true"
      :validation="validation"
      :validation-pattern="validationPattern"
      google-api-key="AIzaSyAIG3db8X-64gITBmCx7WekuKrYrOLvg7k"
      @open-modal="openModal"
      @close-modal="closeModal"
      @geolocate-button="geolocateRequested"
      @radius-change="handleRadiusChange"
      @remove-location="removeLocation"
      @update-location="updateLocation"
    >
      <!-- eslint-enable vue/no-mutating-props -->
      <template #label>
        <cdr-text
          tag="h2"
          class="location-search-modal__facet-title"
        >
          {{ label }}
        </cdr-text>
      </template>
    </location-search>
  </div>
</template>

<style lang="scss">
@use '@/style/components/search/location-search-facet.scss';
</style>
